'use client';

/**
 * GuestV2MenuNav — the Chow mobile menu header, on the web.
 *
 * WHAT WAS WRONG WITH THE V1 NAV. `PlaceMenuNav` renders a row of pills that
 * say "All · 9 · Mains · 4 · Sides · 2". It is a web navigation bar: counts are
 * useful when a guest is choosing where to jump, and the pill is the right shape
 * for a page that already has platform chrome around it. It is not the Chow
 * menu, and a guest who scanned a table QR is looking at the product they
 * already know from the app.
 *
 * SO THIS IS THE APP'S COMPOSITION, TOP TO BOTTOM:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ⓘ Paris Place's own menu design. Tap any... │  mode note, only when a
 *   │                                              │  design body can render
 *   ├──────────────────────────────────────────────┤
 *   │ All   Starters   Mains   Drinks              │  underline tabs, in flow
 *   └──────────────────────────────────────────────┘
 *
 * SEARCH LIVES INSIDE FILTERS. The header's filter icon opens a compact bottom
 * sheet with search and the guest's dietary controls. Category and collection
 * filters stay in the menu. The query combines with the active tab instead of
 * replacing it (see `MenuDisplayPage`), and remains applied when the sheet closes.
 *
 * CATEGORY TABS STAY IN THE MENU FLOW. This keeps the menu compact and follows
 * Chow's screen composition; the filter sheet opens independently from the
 * header without taking up a permanent row.
 *
 * THE DIETARY PILL IS THE GUEST'S, NOT THE MERCHANT'S. `describeItemDietary`
 * flags dishes by default. This pill is the only thing that hides them, and its
 * label says which state it is in — "Showing what you can eat" when on,
 * "Hide what I can't eat" when off. The edit action opens the same profile
 * sheet the app uses; there is no second place to set allergies.
 *
 * @location components/place/GuestV2MenuNav.tsx
 */

import { useRef, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Feather, Search, ShieldCheck, X } from 'lucide-react';

import { cn } from '@/components/ui/utils';
import { GuestV2Segmented } from '@/components/guest-v2/GuestV2';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { zClass } from '@/lib/ui/z-layers';
import type { PlaceMenuViewMode } from '@/lib/customer/place-menu-view';

export type GuestV2MenuSection = {
  id: string | null;
  label: string;
  count?: number;
};

export type GuestV2MenuGroup = {
  id: string | null;
  label: string;
};

type NavProps = {
  /** Used in the mode note, exactly as the app writes it. */
  title: string;
  /** The body currently on screen. */
  view: PlaceMenuViewMode;
  /** Can this branch render a published design at all? */
  canShowDesign: boolean;
  /** A paged template turns pages; the note says so. */
  isBook?: boolean;

  searchOpen: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  /** Header filter control closes the sheet while keeping the query applied. */
  onSearchClose: () => void;

  sections: GuestV2MenuSection[];
  activeSectionId: string | null;
  onSectionChange: (id: string | null) => void;

  /** Collections, only when the branch has more than one. */
  groups?: GuestV2MenuGroup[];
  activeGroupId?: string | null;
  onGroupChange?: (id: string | null) => void;

  hasDietaryProfile: boolean;
  hideDietaryClashes: boolean;
  onToggleDietaryClashes: () => void;
  onEditDietary: () => void;

  /** The shell header's live height when sticky tabs are explicitly enabled. */
  stickyTop?: string;
  /** Chow keeps the menu chrome in the scroll flow. V2 passes false. */
  sticky?: boolean;
  /** Guest palette copied onto the Radix portal root. */
  paletteStyle?: CSSProperties;
};

const ALL_GROUPS = '__all__';

function noteCopy({
  title,
  view,
  canShowDesign,
  isBook,
}: Pick<NavProps, 'title' | 'view' | 'canShowDesign' | 'isBook'>): string | null {
  if (!canShowDesign) return null;
  if (view === 'design') {
    return `${title}’s own menu design${isBook ? ', swipe to turn pages' : ''}. Tap any dish to order.`;
  }
  return 'Simple list. Tap the book icon for the restaurant’s design.';
}

function handleTabKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, ids: (string | null)[]) {
  const direction =
    event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : event.key === 'Home' ? 'home' : event.key === 'End' ? 'end' : 0;
  if (!direction) return;

  event.preventDefault();
  const current = ids.findIndex((id) => id === event.currentTarget.dataset.tabId);
  if (current < 0) return;
  const nextIndex =
    direction === 'home'
      ? 0
      : direction === 'end'
        ? ids.length - 1
        : (current + direction + ids.length) % ids.length;
  const next = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex];
  next?.focus();
}

export function GuestV2MenuNav({
  title,
  view,
  canShowDesign,
  isBook = false,
  searchOpen,
  query,
  onQueryChange,
  onSearchClose,
  sections,
  activeSectionId,
  onSectionChange,
  groups = [],
  activeGroupId = null,
  onGroupChange,
  hasDietaryProfile,
  hideDietaryClashes,
  onToggleDietaryClashes,
  onEditDietary,
  stickyTop = 'var(--place-header-height, 64px)',
  sticky = true,
  paletteStyle,
}: NavProps) {
  const searchRef = useRef<HTMLInputElement>(null);
  const note = noteCopy({ title, view, canShowDesign, isBook });

  // Chow shows All only when there is more than one category. One category is
  // its own tab; zero categories is no tab row at all.
  const categories = sections.filter((section) => section.id !== null);
  const tabs: GuestV2MenuSection[] =
    categories.length > 1
      ? [{ id: null, label: 'All' }, ...categories]
      : categories;
  const tabIds = tabs.map((tab) => tab.id);
  const hasTabs = tabs.length > 0;

  const groupOptions = groups.map((group) => ({
    value: group.id ?? ALL_GROUPS,
    label: group.label,
  }));
  const activeGroupValue = activeGroupId ?? ALL_GROUPS;
  const hasGroups = groups.length > 1;
  const hasStickyContent = hasGroups || hasTabs;

  return (
    <div className="gv-menu-nav">
      <div className="gv-container">
        {note ? (
          <p className="gv-menu-mode">
            <Feather aria-hidden="true" className="size-3.5 shrink-0" />
            <span>{note}</span>
          </p>
        ) : null}

      </div>

      <Sheet open={searchOpen} onOpenChange={(open) => !open && onSearchClose()}>
        <SheetContent
          side="bottom"
          className="chow-root gv-menu-filter-sheet"
          aria-describedby="gv-menu-filter-description"
          style={paletteStyle}
        >
          <div className="gv-menu-filter-sheet__head">
            <SheetTitle className="gv-menu-filter-sheet__title">Filter</SheetTitle>
            <button
              type="button"
              className="gv-menu-filter-sheet__close"
              aria-label="Close filters"
              onClick={onSearchClose}
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </div>
          <SheetDescription id="gv-menu-filter-description" className="sr-only">
            Search dishes and narrow the menu by course or dietary needs.
          </SheetDescription>

          <div className="gv-menu-filter-sheet__body">
            <div className="gv-menu-search" role="search">
              <Search aria-hidden="true" className="size-[18px] shrink-0" />
              <input
                ref={searchRef}
                autoFocus
                id="gv-menu-filter-search"
                type="search"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    onSearchClose();
                  }
                }}
                placeholder="Search dishes"
                aria-label="Search dishes by name or ingredient"
                enterKeyHint="search"
                autoComplete="off"
                spellCheck={false}
              />
              {query ? (
                <button
                  type="button"
                  className="gv-menu-search__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    onQueryChange('');
                    searchRef.current?.focus();
                  }}
                >
                  <X aria-hidden="true" className="size-4" />
                </button>
              ) : null}
            </div>

            {/* Courses are chips here as well as tabs in the menu flow: a guest
                who opened Filter to narrow the menu should not have to close it
                to reach the categories. */}
            {hasTabs ? (
              <section className="gv-menu-filter-group" aria-labelledby="gv-menu-course-title">
                <h3 id="gv-menu-course-title" className="gv-menu-filter-group__title">
                  Course
                </h3>
                <div className="gv-menu-filter-chips">
                  {tabs.map((tab) => {
                    const active = (tab.id ?? null) === (activeSectionId ?? null);
                    return (
                      <button
                        key={tab.id ?? '__all__'}
                        type="button"
                        className="gv-menu-filter-chip"
                        aria-pressed={active}
                        onClick={() => onSectionChange(tab.id ?? null)}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {hasDietaryProfile ? (
              <section className="gv-menu-filter-group" aria-labelledby="gv-menu-dietary-title">
                <h3 id="gv-menu-dietary-title" className="gv-menu-filter-group__title">
                  Dietary
                </h3>
                <div className="gv-menu-filter-toggle">
                  <div className="gv-menu-filter-toggle__copy">
                    <p className="gv-menu-filter-toggle__title">Hide dishes that conflict</p>
                    <p className="gv-menu-filter-toggle__body">
                      Matched against your dietary profile
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hideDietaryClashes}
                    aria-label="Hide dishes that conflict with your dietary profile"
                    className="gv-menu-filter-switch"
                    onClick={onToggleDietaryClashes}
                  >
                    <span aria-hidden="true" />
                  </button>
                </div>
                <button
                  type="button"
                  className="gv-menu-diet__edit"
                  onClick={() => {
                    onSearchClose();
                    onEditDietary();
                  }}
                >
                  <ShieldCheck aria-hidden="true" className="size-4 shrink-0" />
                  Edit dietary preferences
                </button>
              </section>
            ) : null}
          </div>

          <div className="gv-menu-filter-sheet__footer">
            <button
              type="button"
              className="gv-menu-filter-sheet__reset"
              disabled={!query && !hideDietaryClashes && activeSectionId === null}
              onClick={() => {
                onQueryChange('');
                if (hideDietaryClashes) onToggleDietaryClashes();
                if (activeSectionId !== null) onSectionChange(null);
              }}
            >
              Reset
            </button>
            <button type="button" className="gv-menu-filter-sheet__done" onClick={onSearchClose}>
              Show results
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {hasStickyContent ? (
        <div
          className={cn(
            sticky ? 'gv-menu-nav__sticky' : 'gv-menu-nav__flow',
            sticky && 'sticky',
            sticky && zClass('sticky'),
          )}
          style={sticky ? { top: stickyTop } : undefined}
        >
          {hasGroups && onGroupChange ? (
            <div className="gv-container gv-menu-groups">
              <GuestV2Segmented
                label="Menu collections"
                options={groupOptions}
                value={activeGroupValue}
                onChange={(value) => onGroupChange(value === ALL_GROUPS ? null : value)}
              />
            </div>
          ) : null}

          {hasTabs ? (
            <div className="gv-menu-tabs">
              <div className="gv-container">
                <div role="tablist" aria-label="Menu categories" className="gv-menu-tabs__row">
                  {tabs.map((tab) => {
                    const active = (tab.id ?? null) === (activeSectionId ?? null);
                    return (
                      <button
                        key={tab.id ?? '__all__'}
                        type="button"
                        role="tab"
                        data-tab-id={tab.id ?? ''}
                        aria-selected={active}
                        tabIndex={active ? 0 : -1}
                        className={cn('gv-menu-tab', active && 'is-active')}
                        onClick={() => onSectionChange(tab.id ?? null)}
                        onKeyDown={(event) => handleTabKeyDown(event, tabIds)}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}

        </div>
      ) : null}
    </div>
  );
}
